import React from 'react'
import { useTranslation } from 'react-i18next'
import { CalmIllustration } from '../CalmIllustration'
import { EmptyState } from '../EmptyState'
import { useStableRandomIndex } from '../../hooks/useStableRandomIndex'
import { ListToolbar } from '../ListToolbar'
import type { CanvasEmptyStateTeaserProps } from './CanvasEmptyStateTeaser.types'
import './CanvasEmptyStateTeaser.scss'

/** So viele Varianten stehen unter `project.emptyTitles` in den Sprachdateien. */
const EMPTY_TITLE_COUNT = 8

/**
 * Der leere Zustand eines frischen Projekts — steht anstelle des Aufgabenbretts,
 * solange es weder Aufgaben noch Zettel gibt (`shouldShowCanvasEmptyState`).
 *
 * **Umgebaut am 09.09.2026:** Hier stand eine gestrichelte Kachel mit Plus und
 * „Neue Aufgabe" — ein Knopf ohne Begrüßung, in Werkzeug-Sprache. Das leere
 * Blatt ist aber der Moment, in dem Leute steckenbleiben; der Text nimmt jetzt
 * den Anspruch heraus, und der zweite Weg führt in den Canvas, für die, die
 * erst denken und dann ordnen wollen. Gestalt: `EmptyState`, siehe
 * `.claude/tasks/empty-states-ruhend-projekt.md`.
 *
 * **Umgebaut am 09.09.2026 (zweiter Schritt):** aus dem Medaillon wurde die
 * Karten-Variante. Sie bringt ihren eigenen abgesetzten Grund mit — auf
 * weißem Grund wäre ein weißer Kasten mit Kontur nur ein Rahmen. Der Grund
 * gehört dem leeren Zustand und endet mit ihm: ein leeres Projekt zeigt einen
 * Kasten auf abgesetzter Fläche, ein gefülltes zeigt Zeilen auf Weiß. Siehe
 * `.claude/tasks/empty-state-karte-und-tab-grund.md`.
 *
 * **„Gedanken sammeln" ist mit #229 weggefallen.** Statt der zwei Knöpfe steht
 * hier die **Listen-Werkzeug-Pille** in ihrer leeren Gestalt: Aufgabe,
 * Abschnitt, Notiz, Memo. Notiz und Memo holen die Pinnwand maximiert herein
 * und hängen den Zettel gleich an den Zeiger — der frühere Weg öffnete sie
 * leer und ohne Werkzeug.
 *
 * **Jeder Knopf geht über `leave`.** Die Box fährt erst auf ihrem
 * Ankunftsweg zurück hinaus, dann feuert die Aktion. Ohne das verschwand sie
 * von einem Frame zum nächsten: Der Klick nimmt ihr die Daseinsberechtigung
 * (`isCreatingTask` bzw. `canvasIsOpen`), der Knoten fällt weg — und ein
 * `exit` hätte niemanden, der es abspielt, weil über dem leeren Zustand kein
 * `AnimatePresence` steht. **Nur der Zug an Notiz/Memo nicht**: Er hängt den
 * Zettel sofort an die Hand, und die maximierte Pinnwand schiebt die Box weg.
 *
 * **Umgebaut am 25.09.2026 (dritter Schritt):** Statt der Karte mit Motiv
 * steht hier dieselbe Gestalt wie auf Geschafft, Ruhend und Wiederkehrend:
 * ein zufälliges Motiv (`CalmIllustration set="project"`), einer von acht
 * Titeln, die gestaffelte Einblendung. Die Knöpfe stehen jetzt untereinander:
 * „Aufgabe erstellen“ als Hauptweg (primär), „Gedanken sammeln“ darunter.
 * Siehe `.claude/tasks/project-empty-state-illustration.md`.
 *
 * @example
 * <CanvasEmptyStateTeaser
 *   onCreateTask={() => startCreating('top', 0)}
 *   onCreateSection={() => handleAddSection('bottom')}
 *   onPlaceNote={() => requestCanvasPlacement('note')}
 *   onPlaceMemo={() => requestCanvasPlacement('memo')}
 * />
 */
export const CanvasEmptyStateTeaser: React.FC<CanvasEmptyStateTeaserProps> = ({
  onCreateTask,
  onCreateSection,
  onPlaceNote,
  onPlaceMemo,
  onNotePointerDown,
  onMemoPointerDown,
  className,
}) => {
  const { t } = useTranslation()
  const emptyTitleIndex = useStableRandomIndex(EMPTY_TITLE_COUNT)

  return (
    <EmptyState
      className={['canvas-empty-teaser', className].filter(Boolean).join(' ')}
      illustration={<CalmIllustration set="project" />}
      title={t(`project.emptyTitles.${emptyTitleIndex}`)}
      text={t('project.emptyText')}
      actionsLayout="stack"
      actions={(leave) => (
        // Der Schein am unteren Rand hängt an der Pille (`empty-state-glow-anchor`).
        <ListToolbar
          variant="emptyState"
          className="empty-state-glow-anchor"
          onAddTask={() => leave(onCreateTask)}
          onAddSection={() => leave(onCreateSection)}
          onPlaceNote={onPlaceNote ? () => leave(onPlaceNote) : undefined}
          onPlaceMemo={onPlaceMemo ? () => leave(onPlaceMemo) : undefined}
          onNotePointerDown={onNotePointerDown}
          onMemoPointerDown={onMemoPointerDown}
        />
      )}
    />
  )
}
